import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const Toast = ({ 
  message, 
  type = 'success', 
  duration = 5000, 
  onClose, 
  position = 'top-right' 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose && onClose();
    }, 300);
  };

  if (!isVisible) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <FaCheckCircle />;
      case 'error':
        return <FaExclamationCircle />;
      case 'info':
        return <FaInfoCircle />;
      default:
        return <FaInfoCircle />;
    }
  };

  return (
    <ToastContainer 
      type={type} 
      position={position}
      isLeaving={isLeaving}
    >
      <ToastContent>
        <ToastIcon type={type}>
          {getIcon()}
        </ToastIcon>
        <ToastMessage>{message}</ToastMessage>
        <CloseButton onClick={handleClose}>
          <FaTimes />
        </CloseButton>
      </ToastContent>
      <ToastProgress type={type} duration={duration} />
    </ToastContainer>
  );
};

// Toast Container Component
export const ToastWrapper = ({ children, position = 'top-right' }) => {
  return (
    <ToastContainerWrapper position={position}>
      {children}
    </ToastContainerWrapper>
  );
};

// Toast Hook for easy usage
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'success', duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);
    
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showSuccess = (message, duration) => showToast(message, 'success', duration);
  const showError = (message, duration) => showToast(message, 'error', duration);
  const showInfo = (message, duration) => showToast(message, 'info', duration);

  return {
    toasts,
    showToast,
    showSuccess,
    showError,
    showInfo,
    removeToast
  };
};

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const progressAnimation = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

// Styled Components
const ToastContainerWrapper = styled.div`
  position: fixed;
  z-index: 10000;
  
  ${props => {
    switch (props.position) {
      case 'top-left':
        return `
          top: 20px;
          left: 20px;
        `;
      case 'top-center':
        return `
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'top-right':
        return `
          top: 20px;
          right: 20px;
        `;
      case 'bottom-left':
        return `
          bottom: 20px;
          left: 20px;
        `;
      case 'bottom-center':
        return `
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'bottom-right':
        return `
          bottom: 20px;
          right: 20px;
        `;
      default:
        return `
          top: 20px;
          right: 20px;
        `;
    }
  }}
  
  @media (max-width: 768px) {
    left: 10px;
    right: 10px;
    transform: none;
  }
`;

const ToastContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
  min-width: 300px;
  max-width: 400px;
  overflow: hidden;
  animation: ${props => props.isLeaving ? slideOut : slideIn} 0.3s ease-in-out;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          border-left: 4px solid #10B981;
        `;
      case 'error':
        return `
          border-left: 4px solid #EF4444;
        `;
      case 'info':
        return `
          border-left: 4px solid #3B82F6;
        `;
      default:
        return `
          border-left: 4px solid #6B7280;
        `;
    }
  }}
  
  @media (max-width: 768px) {
    min-width: auto;
    max-width: none;
    width: 100%;
  }
`;

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 12px;
`;

const ToastIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `color: #10B981;`;
      case 'error':
        return `color: #EF4444;`;
      case 'info':
        return `color: #3B82F6;`;
      default:
        return `color: #6B7280;`;
    }
  }}
`;

const ToastMessage = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    color: #6B7280;
    background: #F3F4F6;
  }
`;

const ToastProgress = styled.div`
  height: 3px;
  background: ${props => {
    switch (props.type) {
      case 'success':
        return '#10B981';
      case 'error':
        return '#EF4444';
      case 'info':
        return '#3B82F6';
      default:
        return '#6B7280';
    }
  }};
  animation: ${progressAnimation} ${props => props.duration}ms linear;
`;

export default Toast;
